import type { Confirmation, ConfirmationStatus } from "../types/confirmations.types";
import type {
  PaymentConfirmationItem,
  ConfirmationUpdatedPayload,
  OperationStatus,
} from "../types/confirmations-api.types";

function mapApiStatusToConfirmationStatus(status: OperationStatus): ConfirmationStatus {
  if (status === "CONFIRMED" || status === "NOTIFIED") return "confirmed";
  return "pending";
}

function normalizeUrlsAnexos(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.filter((u): u is string => typeof u === "string" && u.length > 0);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return Array.isArray(parsed)
          ? parsed.filter((u): u is string => typeof u === "string" && u.length > 0)
          : [trimmed];
      } catch {
        return [trimmed];
      }
    }
    return [trimmed];
  }
  return [];
}

/**
 * Maps a PaymentConfirmationItem from the API to the UI Confirmation model
 * used by ConfirmationCard and ConfirmationsBoard.
 */
export function mapPaymentConfirmationToConfirmation(
  item: PaymentConfirmationItem
): Confirmation {
  const attachmentUrls = normalizeUrlsAnexos(item.urls_anexos);
  return {
    id: item.id,
    name: item.client_name,
    amount: item.amount,
    currency: item.currency,
    status: mapApiStatusToConfirmationStatus(item.status),
    payer: item.payer_name,
    platform: item.bank,
    date: item.payment_date,
    referenceCode: item.reference_code ?? undefined,
    createdAt: item.created_at ?? undefined,
    updatedAt: item.updated_at ?? undefined,
    note: item.notes ?? undefined,
    createdBy: item.user?.email ?? undefined,
    updatedByEmail: item.updated_by_user?.email ?? undefined,
    verifiedByAdmin: item.status === "CONFIRMED" && item.taken_by_user != null,
    attachmentUrl: item.attachment_url ?? undefined,
    attachmentUrls: attachmentUrls.length > 0 ? attachmentUrls : undefined,
  };
}

/**
 * Maps the confirmation:updated socket event payload to the UI Confirmation model.
 * Used for real-time update without refetch.
 */
export function mapConfirmationUpdatedPayloadToConfirmation(
  payload: ConfirmationUpdatedPayload
): Confirmation {
  const amount =
    payload.amount != null
      ? typeof payload.amount === "number"
        ? String(payload.amount)
        : payload.amount
      : "";
  const attachmentUrls = normalizeUrlsAnexos(payload.urls_anexos);
  const status = mapApiStatusToConfirmationStatus(payload.status);
  return {
    id: payload.id,
    name: payload.client_name ?? "",
    amount,
    currency: payload.currency ?? "",
    status,
    payer: payload.payer_name ?? "",
    platform: payload.bank ?? "",
    date: payload.payment_date ?? "",
    referenceCode: payload.reference_code ?? undefined,
    createdAt: payload.created_at ?? undefined,
    updatedAt: payload.updated_at ?? undefined,
    note: payload.notes ?? undefined,
    createdBy: payload.user?.email ?? undefined,
    updatedByEmail: payload.updated_by_user?.email ?? undefined,
    verifiedByAdmin:
      payload.status === "CONFIRMED" && payload.taken_by_user != null,
    attachmentUrl: payload.attachment_url ?? undefined,
    attachmentUrls: attachmentUrls.length > 0 ? attachmentUrls : undefined,
  };
}
